import { Alert, Box, Stack, TextField } from '@mui/material';
import { UserIdentityProvider, Visitor } from 'graphql/generated/graphqlTypes';
import useAddOrEditVisitor from 'hooks/allVisitors/drawers/useAddOrEditVisitor';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { getVisitorGroupById, VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { VISITOR_NAME_MAX_LENGTH } from 'v2/constants';
import { getUserPath } from 'v2/paths';
import { emailRegex } from 'v2/utils/validation';
import { VisitorGroupAutocompleteOption } from 'v2/utils/visitorGroupAutocomplete';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';
import ExpirationDateField from './ExpirationDateField';
import RolesSection from './RolesSection';
import { getUserFormDefaultValues, getVisitorMutationInput, UserFormValues } from './userFormValues';

interface Props {
  preSelectedGroup?: VisitorGroupAutocompleteOption | null;
  visitor?: Visitor;
  topContent?: ReactNode;
  isMaxNumberOfUsersReached?: boolean;
}

const FORM_ID = 'add-user-drawer-form';

const UserFormDrawer = ({ preSelectedGroup, visitor, topContent, isMaxNumberOfUsersReached }: Props) => {
  const isEditMode = visitor !== undefined;
  const isAddMode = !isEditMode;
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const {
    existingEmails,
    visitorGroupsLoading,
    addOrEditVisitor,
    administratorsVisitorGroups,
    installersVisitorGroups
  } = useAddOrEditVisitor({
    translation,
    handleFetchError: setError
  });

  const preSelectedVisitorGroup =
    preSelectedGroup && administratorsVisitorGroups && installersVisitorGroups
      ? getVisitorGroupById([...administratorsVisitorGroups, ...installersVisitorGroups], preSelectedGroup.value)
      : undefined;
  const isAdministratorsGroup = preSelectedVisitorGroup?.type === VisitorGroupType.Administrators;
  const isInstallersGroup = preSelectedVisitorGroup?.type === VisitorGroupType.Installers;

  const defaultValues = useMemo(
    () => getUserFormDefaultValues({ visitor, preSelectedGroup, isAdministratorsGroup, isInstallersGroup }),
    [visitor, preSelectedGroup, isAdministratorsGroup, isInstallersGroup]
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting, isDirty }
  } = useForm<UserFormValues>({ defaultValues });

  useEffect(() => {
    if (!visitorGroupsLoading) {
      reset(defaultValues);
    }
  }, [visitorGroupsLoading, reset, defaultValues]);

  const isEntraUser = visitor?.identityProvider === UserIdentityProvider.Entra;

  const visitorOrigin = visitor ? getVisitorOrigin(visitor) : undefined;
  const isKeySharingVisitor = visitorOrigin === VisitorOrigin.KeySharing;
  const showRolesSection = !isKeySharingVisitor;

  // The email is read-only when editing, so it is only validated when adding a user.
  const emailValidationRules: RegisterOptions<UserFormValues, 'email'> | undefined = isEditMode
    ? undefined
    : {
        required: translation.email_required_error,
        pattern: { value: emailRegex, message: translation.email_invalid_error },
        validate: (value) => (existingEmails.includes(value) ? translation.email_duplicate_error : true)
      };

  const onSubmit = async (data: UserFormValues) => {
    setError(undefined);
    // Backend rejects SetVisitorByIdWithRole when the membership hasn't actually changed.
    // Skip the call and close cleanly when nothing was edited.
    if (isEditMode && !isDirty) {
      closeDrawer();
      return;
    }
    const result = await addOrEditVisitor(getVisitorMutationInput(data, visitor));
    if (result?.data) {
      showSnackbar(translation.saved_changes, { severity: 'success' });
      closeDrawer();
      const newVisitorId = 'addVisitor' in result.data ? result.data.addVisitor?.id : undefined;
      if (newVisitorId && !preSelectedGroup) {
        navigate(getUserPath(newVisitorId));
      }
    }
  };

  return (
    <DrawerTemplate
      loading={isSubmitting}
      title={isEditMode ? translation.edit_user : translation.add_user}
      primaryAction={{
        label: isEditMode ? translation.save : translation.add,
        onClick: handleSubmit(onSubmit),
        loading: visitorGroupsLoading || isSubmitting,
        form: FORM_ID,
        id: 'drawer-submit-button',
        disabled: isMaxNumberOfUsersReached
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      errorTitle={isEditMode ? translation.could_not_save_changes : translation.could_not_add_user}
      onDismissError={() => setError(undefined)}
    >
      {visitorGroupsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Loader />
        </Box>
      ) : (
        <Stack id={FORM_ID} component="form" onSubmit={handleSubmit(onSubmit)} noValidate gap={3}>
          {topContent}
          <TextField
            {...register('name', {
              required: translation.name_cannot_be_empty,
              maxLength: {
                value: VISITOR_NAME_MAX_LENGTH,
                message: translation.name_max_length_error.replace('{{count}}', String(VISITOR_NAME_MAX_LENGTH))
              }
            })}
            id="name-input"
            label={translation.name}
            variant="outlined"
            slotProps={{ htmlInput: { readOnly: isEntraUser } }}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
          <TextField
            {...register('email', emailValidationRules)}
            id="email-input"
            label={translation.e_mail}
            variant="outlined"
            disabled={isEditMode}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
          {isEntraUser && <Alert severity="info">{translation.managed_through_entra_id}</Alert>}
          {showRolesSection && (
            <RolesSection
              control={control}
              disableAdministratorRole={isAdministratorsGroup || isEntraUser}
              disableInstallerRole={isInstallersGroup}
              administratorDisabledTooltip={
                isEntraUser
                  ? translation.admin_role_entra_id_tooltip
                  : isAdministratorsGroup
                    ? translation.role_implied_by_selected_group
                    : undefined
              }
              installerDisabledTooltip={isInstallersGroup ? translation.role_implied_by_selected_group : undefined}
            />
          )}
          {isAddMode && <ExpirationDateField control={control} />}
        </Stack>
      )}
    </DrawerTemplate>
  );
};

export default UserFormDrawer;
